import { isEmpty, isNil } from 'lodash';
import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { MBTextInput } from '@shared/components/textInput';
import { EmailField } from '@shared/data/entity/fields/EmailField';
import { useComponentFocus } from '@shared/scenes/cards/form/components/ComponentFocus';
import { useResponsiveFormContext } from '@shared/scenes/entity/components/ResponsiveFormContext';
import { getComponentPlaceholder } from '@shared/scenes/workflows/utils';
import { grid } from '@shared/styles';
import { useBoolean } from '@shared/util/hooks';
import { EditFieldContainer } from './EditFieldContainer';

interface Props {
  field: EmailField | null;
  component:
    | MBAPI.WorkflowTemplate_components_EmailComponent
    | MBAPI.WorkflowTemplate_components_RelatedCardLookupComponent;
  updateField?: (componentId: string, field: EmailField | null) => void;
  error?: string;
  allowClear?: boolean;
  showIcon?: boolean;
  label?: string;
  showLabel?: boolean;
  indentLabel?: boolean;
  reserveIconSpace?: boolean;
  compactTop?: boolean;
  compactBottom?: boolean;
  subfooter?: ReactNode;
  isGrayScale?: boolean;
  isItalic?: boolean;
  style?: StyleProp<ViewStyle>;
}

export function EmailEditField({
  field,
  component,
  updateField,
  error,
  allowClear,
  showIcon,
  label,
  showLabel,
  indentLabel,
  reserveIconSpace,
  compactTop,
  compactBottom,
  subfooter,
  isGrayScale,
  isItalic,
  style,
}: Props) {
  const [value, setValue] = useState(field?.emails.join(', ') ?? '');
  const [initialValueSet, setInitialValueSet] = useState(false);
  const [hasInput, onInput, onClearInput] = useBoolean(false);
  const [isFocused, onFocus, onBlur] = useBoolean(false);
  const { setComponentFocused, setComponentBlurred } = useComponentFocus();
  const showLocalError = !isFocused;
  const showError = (!hasInput || showLocalError) && !isEmpty(error);

  const handleFocus = useCallback(() => {
    onFocus();
    setComponentFocused();
  }, [setComponentFocused, onFocus]);

  const handleBlur = useCallback(() => {
    onBlur();
    setComponentBlurred();
  }, [setComponentBlurred, onBlur]);

  useEffect(() => {
    const emails = getEmailsFromInput(value);
    if (isEmpty(emails)) {
      onClearInput();
    } else {
      onInput();
    }
    updateField?.(component.id, !isEmpty(emails) ? new EmailField(emails) : null);
  }, [value, updateField, component.id, onClearInput, onInput]);

  useEffect(() => {
    if (initialValueSet) {
      return;
    }
    if (!isEmpty(field?.emails) && value !== field?.emails.join(', ')) {
      setInitialValueSet(true);
      setValue(field?.emails.join(', ') ?? '');
    }
  }, [field, value, initialValueSet]);

  const onClear = useCallback(() => {
    onClearInput();
    setValue('');
    updateField?.(component.id, null);
  }, [setValue, onClearInput, updateField, component.id]);

  const hasValue = !isEmpty(value);
  const isReadOnly = component.readonly || isNil(updateField);
  const showClearButton = hasValue && !isReadOnly && allowClear;

  const responsiveForms = useResponsiveFormContext();

  return (
    <EditFieldContainer
      component={component}
      showIcon={showIcon}
      label={label}
      showLabel={!isNil(showLabel) ? showLabel : responsiveForms || hasValue}
      indentLabel={indentLabel}
      reserveIconSpace={reserveIconSpace}
      showClearButton={showClearButton}
      onClear={onClear}
      error={showError ? error : undefined}
      compactTop={compactTop}
      compactBottom={compactBottom}
      subfooter={subfooter}
      isGrayScale={isGrayScale}
      isItalic={isItalic}
      style={style}
    >
      <MBTextInput
        noBorder
        noPadding
        inputMode="email"
        textContentType="emailAddress"
        keyboardType="email-address"
        value={value}
        onChangeText={setValue}
        placeholder={getComponentPlaceholder(component)}
        onBlur={handleBlur}
        onFocus={handleFocus}
        editable={!isReadOnly}
        style={{ paddingVertical: isEmpty(value) ? grid(1.25) : undefined }}
      />
    </EditFieldContainer>
  );
}

function getEmailsFromInput(input: string) {
  return input
    .replace(/( )?,( )?/g, ',')
    .trim()
    .split(',')
    .filter(Boolean);
}
